import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '@/hooks/useAppState';
import { useFinanceRefresh } from '@/hooks/useFinanceRefresh';
import TaskItem from '@/components/feature/TaskItem';
import FinanceHotspotCard from '@/components/feature/FinanceHotspotCard';
import QuickAddSheet from '@/components/feature/QuickAddSheet';
import SharePreview from '@/components/feature/SharePreview';
import PrivacyBadge from '@/components/feature/PrivacyBadge';

export default function Home() {
  const {
    tasks,
    projects,
    toggleTask,
    addTask,
    todayImportantTasks,
    todayTasks,
    completedTodayCount,
    openShareModal,
    shareModalOpen,
    shareModalTarget,
    closeShareModal,
    showToast,
    toastMessage,
    clearToast,
  } = useAppState();

  const {
    isRefreshing,
    isStale,
    currentItems,
    formattedLastUpdated,
    triggerRefresh,
  } = useFinanceRefresh((mode) => {
    showToast(mode === 'live' ? '已更新今日财经热点' : '当前为财经演示数据');
    setTimeout(() => clearToast(), 3000);
  });

  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const navigate = useNavigate();

  // Pick top project by progress (most urgent = highest progress active)
  const topProject = useMemo(() => {
    const active = projects.filter((p) => p.status === 'active');
    return active.sort((a, b) => b.progress - a.progress)[0] || null;
  }, [projects]);

  const topFinanceItems = useMemo(
    () => currentItems.filter((h) => h.heat === '高').slice(0, 1),
    [currentItems]
  );

  const shareTarget = shareModalTarget
    ? currentItems.find((h) => h.id === shareModalTarget) || null
    : null;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 9) return '早上好';
    if (hour < 12) return '上午好';
    if (hour < 18) return '下午好';
    return '晚上好';
  };

  const formatDate = () => {
    const now = new Date();
    const weekDays = ['日', '一', '二', '三', '四', '五', '六'];
    return `${now.getFullYear()}年${now.getMonth() + 1}月${now.getDate()}日 星期${weekDays[now.getDay()]}`;
  };

  // Compact task list: top 2 important or top 2 today
  const top2Tasks = useMemo(() => {
    if (todayImportantTasks.length >= 2) return todayImportantTasks.slice(0, 2);
    if (todayImportantTasks.length === 1) {
      const rest = todayTasks.filter((t) => t.priority !== '高');
      return [todayImportantTasks[0], rest[0]].filter(Boolean);
    }
    return todayTasks.slice(0, 2);
  }, [todayImportantTasks, todayTasks]);

  const remainingTaskCount = todayTasks.length - top2Tasks.length;
  const activeProjectCount = projects.filter((p) => p.status === 'active').length;

  return (
    <div className="min-h-screen bg-background-50 pb-20">
      {/* Header - compact */}
      <header
        className="px-4 pt-4 pb-2"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] text-foreground-400">{formatDate()}</p>
            <h1 className="text-base font-bold text-foreground-800 mt-0 font-heading leading-tight">
              {getGreeting()}，先推进最重要的一步。
            </h1>
          </div>
          <div className="flex items-center gap-1">
            <button className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-notification-3-line text-base text-foreground-500" />
            </button>
            <button className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-search-line text-base text-foreground-500" />
            </button>
          </div>
        </div>

        {/* Compact status bar */}
        <div className="mt-2 flex items-center gap-3 text-[11px] text-foreground-500">
          <span className="flex items-center gap-1">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-checkbox-circle-line text-primary-500 text-xs" />
            </span>
            今日 {todayTasks.length} 项待办
            {completedTodayCount > 0 && (
              <span className="text-accent-600">（已完成 {completedTodayCount}）</span>
            )}
          </span>
          <span className="w-px h-3 bg-background-300" />
          <span className="flex items-center gap-1">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-folder-line text-amber-500 text-xs" />
            </span>
            {activeProjectCount} 个项目推进中
          </span>
          <span className="w-px h-3 bg-background-300" />
          <span className="flex items-center gap-1">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-funds-line text-accent-500 text-xs" />
            </span>
            {topFinanceItems.length} 条财经热点
          </span>
        </div>
      </header>

      <main className="px-4 space-y-3">
        {/* Card 1: Today's Most Important Tasks - compact */}
        <section>
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-foreground-800 font-heading">今日行动</h2>
              <PrivacyBadge type="private" />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setQuickAddOpen(true)}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 hover:bg-primary-100 transition-colors cursor-pointer"
              >
                <i className="ri-add-line text-sm" />
              </button>
              <button
                onClick={() => navigate('/todo')}
                className="text-[11px] text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                全部待办
              </button>
            </div>
          </div>

          <div className="bg-background-50 rounded-xl border border-background-200/60 overflow-hidden">
            {top2Tasks.length > 0 ? (
              top2Tasks.map((task) => (
                <TaskItem key={task.id} task={task} onToggle={toggleTask} compact />
              ))
            ) : (
              <div className="px-4 py-4 text-center">
                <p className="text-xs text-foreground-400">今天还没有待办，轻松一天也不错</p>
              </div>
            )}
            {remainingTaskCount > 0 && (
              <button
                onClick={() => navigate('/todo')}
                className="block w-full px-4 py-2 text-center text-[11px] text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
              >
                还有 {remainingTaskCount} 项待办 · 查看全部
              </button>
            )}
          </div>
        </section>

        {/* Card 2: Single Project Summary - compact */}
        <section>
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-foreground-800 font-heading">项目推进</h2>
              <PrivacyBadge type="private" />
            </div>
            <button
              onClick={() => navigate('/projects')}
              className="text-[11px] text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              全部项目
            </button>
          </div>

          {topProject ? (
            <button
              onClick={() => navigate(`/projects/${topProject.id}`)}
              className="w-full text-left bg-background-50 rounded-xl px-4 py-3 border border-background-200/60 hover:border-primary-200 transition-all duration-200 cursor-pointer group"
            >
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-5 h-5 rounded bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-folder-3-line text-primary-500 text-[10px]" />
                  </span>
                  <span className="text-sm font-semibold text-foreground-800 truncate font-heading">
                    {topProject.name}
                  </span>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-50 text-primary-600 font-medium whitespace-nowrap flex-shrink-0 ml-2">
                  {topProject.stage}
                </span>
              </div>

              {/* Progress */}
              <div className="flex items-center gap-3 mb-2">
                <div className="flex-1 h-1.5 bg-background-200 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary-500 transition-all duration-500"
                    style={{ width: `${topProject.progress}%` }}
                  />
                </div>
                <span className="text-[11px] font-semibold text-foreground-700 whitespace-nowrap">
                  {topProject.progress}%
                </span>
              </div>

              {/* Next step - most prominent */}
              <div className="flex items-start gap-1.5">
                <span className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-arrow-right-circle-line text-accent-500 text-xs" />
                </span>
                <span className="text-xs text-accent-600 font-medium leading-snug">
                  下一步：{topProject.nextStep}
                </span>
              </div>

              {/* Subtle meta */}
              <div className="flex items-center gap-3 mt-1.5 text-[10px] text-foreground-400">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line text-[10px]" />
                  {topProject.deadline}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-task-line text-[10px]" />
                  {topProject.taskCount} 项关联待办
                </span>
              </div>
            </button>
          ) : (
            <div className="bg-background-50 rounded-xl px-4 py-4 border border-background-200/60 text-center">
              <p className="text-xs text-foreground-400">暂无进行中项目</p>
            </div>
          )}
        </section>

        {/* Card 3: Finance Hotspots - moved up, compact, with refresh */}
        <section className="pb-2">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-foreground-800 font-heading">财经热点</h2>
              <PrivacyBadge type="shareable" />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={triggerRefresh}
                disabled={isRefreshing}
                className={`flex items-center gap-1 text-[11px] font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isRefreshing
                    ? 'text-foreground-400'
                    : 'text-primary-500 hover:text-primary-600'
                }`}
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={`ri-refresh-line ${isRefreshing ? 'animate-spin' : ''}`} />
                </span>
                {isRefreshing ? '更新中…' : '更新热点'}
              </button>
              <button
                onClick={() => navigate('/finance')}
                className="text-[11px] text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                全部
              </button>
            </div>
          </div>

          {/* Update time */}
          <div className="flex items-center gap-2 mb-2">
            <span className="flex items-center gap-1 text-[10px] text-foreground-400">
              <span className={`w-1.5 h-1.5 rounded-full ${isStale ? 'bg-amber-400' : 'bg-accent-400'}`} />
              {formattedLastUpdated}
            </span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-background-100 text-foreground-400">
              演示数据
            </span>
          </div>

          <div className="space-y-2">
            {topFinanceItems.map((hotspot) => (
              <div
                key={hotspot.id}
                className="bg-background-100 rounded-xl border-l-2 border-accent-300 overflow-hidden"
              >
                <FinanceHotspotCard
                  hotspot={hotspot}
                  compact
                  onShare={(h) => openShareModal(h.id)}
                />
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* Quick Add Sheet */}
      <QuickAddSheet
        open={quickAddOpen}
        onClose={() => setQuickAddOpen(false)}
        onAdd={addTask}
        projects={projects
          .filter((p) => p.status === 'active')
          .map((p) => ({ id: p.id, name: p.name }))}
      />

      {/* Share Preview */}
      {shareTarget && (
        <SharePreview
          hotspot={shareTarget}
          open={shareModalOpen}
          onClose={closeShareModal}
        />
      )}

      {/* Toast */}
      {toastMessage && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-fade-in">
          <div className="px-4 py-2 rounded-lg bg-foreground-800 text-background-50 text-xs font-medium shadow-lg flex items-center gap-2">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-check-line text-sm" />
            </span>
            {toastMessage}
          </div>
        </div>
      )}
    </div>
  );
}
